import { useRef } from 'react';
import type { VideoOverlay } from '../types';

interface VideoEditorToolbarProps {
  overlays: VideoOverlay[];
  selectedId: string | null;
  onSelectOverlay: (id: string | null) => void;
  onAddText: (preset: 'headline' | 'subheadline') => void;
  onAddCta: () => void;
  onAddLogo: (file: File) => void;
  onDeleteOverlay: (id: string) => void;
  onDuplicateOverlay: (id: string) => void;
  onBack: () => void;
}

const PRESET_LABELS = {
  headline: 'Headline',
  subheadline: 'Subheadline',
  'cta-text': 'CTA Text',
};

const TYPE_ICONS: Record<string, string> = {
  text: 'ri-text',
  cta: 'ri-cursor-line',
  logo: 'ri-image-2-line',
};

export default function VideoEditorToolbar({
  overlays,
  selectedId,
  onSelectOverlay,
  onAddText,
  onAddCta,
  onAddLogo,
  onDeleteOverlay,
  onDuplicateOverlay,
  onBack,
}: VideoEditorToolbarProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onAddLogo(file);
    e.target.value = '';
  };

  return (
    <div className="w-56 bg-stone-900 border-r border-stone-800 flex flex-col overflow-hidden shrink-0">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-stone-800">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-stone-400 hover:text-stone-200 text-xs font-medium transition-colors cursor-pointer mb-3 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          Back
        </button>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-orange-400">
            <i className="ri-film-fill text-white text-xs" />
          </div>
          <span className="text-sm font-bold text-white whitespace-nowrap">Video Editor</span>
        </div>
      </div>

      {/* Add Elements */}
      <div className="px-3 py-3 border-b border-stone-800">
        <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2.5">Add Elements</p>

        <div className="space-y-1.5">
          {/* Headline */}
          <button
            onClick={() => onAddText('headline')}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white text-xs font-semibold transition-all cursor-pointer group whitespace-nowrap"
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-stone-700 group-hover:bg-orange-500/20 transition-colors shrink-0">
              <i className="ri-heading text-orange-400 text-sm" />
            </div>
            <div className="text-left">
              <div className="font-bold text-stone-200">Add Text</div>
              <div className="text-stone-500 text-xs font-normal">Headline / Subheadline</div>
            </div>
          </button>

          {/* CTA Button */}
          <button
            onClick={onAddCta}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white text-xs font-semibold transition-all cursor-pointer group whitespace-nowrap"
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-stone-700 group-hover:bg-orange-500/20 transition-colors shrink-0">
              <i className="ri-cursor-line text-orange-400 text-sm" />
            </div>
            <div className="text-left">
              <div className="font-bold text-stone-200">Add CTA Button</div>
              <div className="text-stone-500 text-xs font-normal">Call-to-action</div>
            </div>
          </button>

          {/* Logo Upload */}
          <button
            onClick={() => logoInputRef.current?.click()}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white text-xs font-semibold transition-all cursor-pointer group whitespace-nowrap"
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-stone-700 group-hover:bg-orange-500/20 transition-colors shrink-0">
              <i className="ri-image-2-line text-orange-400 text-sm" />
            </div>
            <div className="text-left">
              <div className="font-bold text-stone-200">Add Logo</div>
              <div className="text-stone-500 text-xs font-normal">Upload image</div>
            </div>
          </button>
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
        </div>
      </div>

      {/* Text Presets */}
      <div className="px-3 py-3 border-b border-stone-800">
        <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2.5">Text Presets</p>
        <div className="space-y-1">
          <button
            onClick={() => onAddText('headline')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-800 text-stone-400 hover:text-white text-xs transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-font-size-2 text-stone-500" />
            <span className="font-bold text-base text-stone-300">Headline</span>
          </button>
          <button
            onClick={() => onAddText('subheadline')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-stone-800 text-stone-400 hover:text-white text-xs transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-font-size text-stone-500" />
            <span className="text-sm text-stone-400">Subheadline</span>
          </button>
        </div>
      </div>

      {/* Layers */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Layers</p>
          <span className="text-xs text-stone-600 bg-stone-800 px-1.5 py-0.5 rounded-full">{overlays.length}</span>
        </div>

        {overlays.length === 0 ? (
          <div className="text-center py-6">
            <i className="ri-stack-line text-stone-700 text-2xl" />
            <p className="text-stone-600 text-xs mt-2">No layers yet</p>
          </div>
        ) : (
          <div className="space-y-1">
            {[...overlays].reverse().map((overlay) => {
              const isSelected = selectedId === overlay.id;
              return (
                <div
                  key={overlay.id}
                  onClick={() => onSelectOverlay(overlay.id)}
                  className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-orange-500/20 border border-orange-500/30'
                      : 'hover:bg-stone-800 border border-transparent'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`${TYPE_ICONS[overlay.type] ?? 'ri-square-line'} text-xs ${isSelected ? 'text-orange-400' : 'text-stone-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${isSelected ? 'text-orange-300' : 'text-stone-400'}`}>
                      {overlay.type === 'logo' ? 'Logo' : overlay.content || `${overlay.preset ? PRESET_LABELS[overlay.preset] : overlay.type}`}
                    </p>
                    <p className="text-xs text-stone-600 capitalize">{overlay.type}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); onDuplicateOverlay(overlay.id); }}
                      className="w-5 h-5 flex items-center justify-center rounded text-stone-500 hover:text-stone-300 hover:bg-stone-700 transition-colors cursor-pointer"
                      title="Duplicate"
                    >
                      <i className="ri-file-copy-line text-xs" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteOverlay(overlay.id); }}
                      className="w-5 h-5 flex items-center justify-center rounded text-stone-500 hover:text-red-400 hover:bg-stone-700 transition-colors cursor-pointer"
                      title="Delete"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
